import styles from 'css/src/components/public-pages/TrustPage.module.scss';

import React, { useState } from 'react';

import Tooltip from 'js/src/components/common/ui/Tooltip';

type Resource = {
    title: string;
    description?: string;
    file?: string;
    requestOnly?: boolean;
};

type ResourceGroup = {
    heading: string;
    resources: Resource[];
};

const RESOURCE_GROUPS: ResourceGroup[] = [
    {
        heading: 'Audits & Reports',
        resources: [
            {
                title: 'Lumen5 Security Report',
                description:
                    'A summary of our current security posture, compliance tests, and monitored controls.',
                requestOnly: true,
            },
            {
                title: 'Penetration Test Report',
                description:
                    'Most recent third-party penetration test report covering the Lumen5 platform.',
                requestOnly: true,
            },
            {
                title: 'GCP SOC 2 Report',
                description: "SOC 2 report for Google Cloud Platform, Lumen5's hosting provider.",
                file: 'gcp-soc-2-report-2025.pdf',
            },
        ],
    },
    {
        heading: 'Privacy & Data Handling',
        resources: [
            {
                title: 'Privacy and Security Document',
                file: 'privacy-and-security-document.pdf',
            },
            {
                title: 'Data Integrity and Security Policy',
                file: 'data-integrity-and-security-policy.pdf',
            },
            {
                title: 'Data Subprocessors',
                description: 'The list of third parties Lumen5 uses to process customer data.',
                file: 'data-subprocessors.pdf',
            },
            {
                title: 'Data Flow & Architecture Diagram',
                file: 'data-flow-architecture-diagram.pdf',
            },
            {
                title: 'CCPA Compliance',
                file: 'ccpa-compliance.pdf',
            },
        ],
    },
    {
        heading: 'Security Policies',
        resources: [
            {
                title: 'Information Security Policies',
                file: 'information-security-policies.pdf',
            },
            {
                title: 'AI Policy',
                file: 'ai-policy.pdf',
            },
            {
                title: 'Incident Response Policy',
                file: 'incident-response-policy.pdf',
            },
            {
                title: 'Disaster Recovery and Business Continuity',
                file: 'disaster-recovery-and-business-continuity.pdf',
            },
            {
                title: 'Risk Assessment Policy',
                file: 'risk-assessment-policy.pdf',
            },
            {
                title: 'Secure Development Policy',
                file: 'secure-development-policy.pdf',
            },
            {
                title: 'Change Management Policy',
                file: 'change-management-policy.pdf',
            },
            {
                title: 'Third Party Management Policy',
                file: 'third-party-management-policy.pdf',
            },
            {
                title: 'Logging Policy',
                file: 'logging-policy.pdf',
            },
            {
                title: 'Password Policy',
                file: 'password-policy.pdf',
            },
            {
                title: 'Device Security Policy',
                file: 'device-security-policy.pdf',
            },
        ],
    },
    {
        heading: 'People & Ethics',
        resources: [
            {
                title: 'Code of Conduct Policy',
                file: 'code-of-conduct-policy.pdf',
            },
            {
                title: 'Background Check Policy',
                file: 'background-check-policy.pdf',
            },
            {
                title: 'Labour Rights and Workplace Safety',
                file: 'labour-rights-and-workplace-safety.pdf',
            },
            {
                title: 'Anti-Slavery Policy Statement',
                file: 'anti-slavery-policy.pdf',
            },
            {
                title: 'Child Labor Policy',
                file: 'child-labor-policy.pdf',
            },
            {
                title: 'Environmental Sustainability',
                file: 'environmental-sustainability.pdf',
            },
        ],
    },
];

type Faq = {
    question: string;
    answer: React.ReactNode;
};

const FAQS: Faq[] = [
    {
        question: 'What is Lumen5?',
        answer: (
            <p>
                Lumen5 is an online video creation platform. It is a SaaS product (no installed
                software). Generally, companies use Lumen5 to produce marketing or communications
                videos for public distribution using public data.
            </p>
        ),
    },
    {
        question: 'Where does Lumen5 host its data?',
        answer: <p>All our services and data are hosted by Google Cloud Platform in Iowa, USA.</p>,
    },
    {
        question: 'Does Lumen5 provide MFA/2FA and SSO?',
        answer: (
            <p>
                Yes, we provide both multi-factor authentication and single sign-on. For SSO, we
                only support SAML 2.0.
            </p>
        ),
    },
    {
        question: 'What data does Lumen5 collect from customers?',
        answer: (
            <ul>
                <li>Name — for account creation.</li>
                <li>Email — for account creation and authentication.</li>
                <li>
                    Usability data (button clicks, time spent on page, etc.) — to ensure that the
                    system is working as expected and to make improvements.
                </li>
                <li>
                    Media files (text, audio files, video files, image files, and fonts uploaded or
                    inputted to the system as video content) — for video creation.
                </li>
            </ul>
        ),
    },
    {
        question: 'Does Lumen5 provide encryption for both data in transit and data at rest?',
        answer: (
            <p>
                Yes. All traffic from clients is forced through HTTPS, with connections encrypted
                using TLS 1.3 and 256-bit encryption. Data at rest is encrypted with AES-256. More
                info can be found in our{' '}
                <a href="/static/public/trust-center/data-integrity-and-security-policy.pdf">
                    Data Integrity and Security Policy
                </a>
                .
            </p>
        ),
    },
    {
        question: 'Where does Lumen5 keep its data backup?',
        answer: (
            <p>
                Our data backups are held on Google Cloud Platform in Iowa, USA (1430 Veterans
                Memorial Hwy, Council Bluffs, IA 51501, USA).
            </p>
        ),
    },
    {
        question:
            'Does Lumen5 erase customer data when it is no longer required? What is the data retention policy?',
        answer: (
            <p>
                Lumen5 retains data for as long as the customer continues to use our services. If a
                customer chooses to close their account, they can submit a request for data removal.
                For details, please refer to our{' '}
                <a href="/static/public/trust-center/data-integrity-and-security-policy.pdf">
                    Data Integrity and Security Policy
                </a>
                .
            </p>
        ),
    },
    {
        question:
            'In the event of an involuntary shutdown, does Lumen5 provide fast recovery support?',
        answer: (
            <p>
                According to our{' '}
                <a href="/static/public/trust-center/disaster-recovery-and-business-continuity.pdf">
                    Disaster Recovery and Business Continuity policy
                </a>
                , the recovery time objective (RTO) is 48 hours and the recovery point objective
                (RPO) is 24 hours.
            </p>
        ),
    },
    {
        question: 'Does Lumen5 have a SOC 2 or ISO 27001 certification?',
        answer: (
            <>
                <p>
                    Lumen5 does not currently hold a SOC 2 or ISO 27001 certification. However, our
                    customers typically do not require these certifications for the following
                    reasons:
                </p>
                <ul>
                    <li>
                        <strong>Low-risk classification:</strong> Lumen5 is considered low-risk
                        because it is primarily used to create videos intended for public sharing.
                        The data we handle is generally already public.
                    </li>
                    <li>
                        <strong>Certified hosting provider:</strong> Our hosting provider, Google
                        Cloud, holds both SOC 2 and ISO 27001 certifications, and manages a
                        significant portion of the security controls that help keep Lumen5 secure.
                    </li>
                    <li>
                        <strong>ISO 27001-aligned policies:</strong> Although Lumen5 has not
                        undergone a third-party audit, our internal security policies — available on
                        this page — are based on ISO 27001 standards and are available for customer
                        review.
                    </li>
                </ul>
            </>
        ),
    },
];

const RESOURCES_BASE_URL = '/static/public/trust-center';

type ResourceCardProps = {
    resource: Resource;
};

const ResourceCard = React.memo(function ResourceCard({ resource }: ResourceCardProps) {
    const body = (
        <>
            <div className={styles['resource-card__icon']} aria-hidden="true">
                <i translate="no" className="material-symbols-outlined">
                    description
                </i>
            </div>
            <div className={styles['resource-card__body']}>
                <div
                    className={`font-l5 font-l5--regular--semibold ${styles['resource-card__title']}`}
                >
                    {resource.title}
                </div>
                {resource.description && (
                    <div
                        className={`font-l5 font-l5--small ${styles['resource-card__description']}`}
                    >
                        {resource.description}
                    </div>
                )}
            </div>
            {resource.requestOnly ? (
                <div className={styles['resource-card__request']} aria-hidden="true">
                    <i translate="no" className="material-symbols-outlined">
                        lock
                    </i>
                </div>
            ) : (
                <div className={styles['resource-card__action']} aria-hidden="true">
                    <i translate="no" className="material-symbols-outlined">
                        download
                    </i>
                </div>
            )}
        </>
    );

    if (resource.requestOnly) {
        return (
            <Tooltip title="Available upon request">
                <div
                    className={`${styles['resource-card']} ${styles['resource-card--request-only']}`}
                    aria-label="Available upon request"
                >
                    {body}
                </div>
            </Tooltip>
        );
    }

    return (
        <a
            className={styles['resource-card']}
            href={`${RESOURCES_BASE_URL}/${resource.file}`}
            target="_blank"
            rel="noopener noreferrer"
        >
            {body}
        </a>
    );
});

type FaqItemProps = {
    faq: Faq;
};

const FaqItem = React.memo(function FaqItem({ faq }: FaqItemProps) {
    const [open, setOpen] = useState(false);
    return (
        <div className={styles['faq-item']}>
            <button
                type="button"
                className={`font-l5 font-l5--regular ${styles['faq-item__question']}`}
                aria-expanded={open}
                onClick={() => setOpen((current) => !current)}
            >
                <span>{faq.question}</span>
                <i
                    translate="no"
                    className={`material-symbols-outlined ${styles['faq-item__chevron']}`}
                >
                    {open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                </i>
            </button>
            {open && (
                <div className={`font-l5 font-l5--small ${styles['faq-item__answer']}`}>
                    {faq.answer}
                </div>
            )}
        </div>
    );
});

const TrustPage = React.memo(function TrustPage() {
    return (
        <div className={styles['trust-page']}>
            <section className={styles['trust-page__hero']}>
                <div className="container">
                    <div className="row">
                        <div className="col-sm-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2 text-center">
                            <h6
                                className={`font-l5 font-l5--overline ${styles['trust-page__eyebrow']}`}
                            >
                                Trust Center
                            </h6>
                            <h1 className={styles['trust-page__title']}>Security and trust</h1>
                            <p
                                className={`font-l5 font-l5--subtitle-l ${styles['trust-page__subtitle']}`}
                            >
                                We take the security and privacy of our customers&apos; data
                                seriously. Below are the policies, reports, and documentation that
                                describe how Lumen5 protects your information.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className={styles['trust-page__section']}>
                <div className="container">
                    <div className="row">
                        <div className="col-sm-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
                            <h2 className={styles['trust-page__section-title']}>
                                Frequently asked questions
                            </h2>
                            <div className={styles['faq-list']}>
                                {FAQS.map((faq) => (
                                    <FaqItem key={faq.question} faq={faq} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section
                className={`${styles['trust-page__section']} ${styles['trust-page__section--muted']}`}
            >
                <div className="container">
                    <div className="row">
                        <div className="col-sm-12 col-md-10 offset-md-1">
                            <h2 className={styles['trust-page__section-title']}>Resources</h2>
                            <p
                                className={`font-l5 font-l5--regular ${styles['trust-page__section-subtitle']}`}
                            >
                                Download the documentation most relevant to your review.
                            </p>

                            {RESOURCE_GROUPS.map((group) => (
                                <div key={group.heading} className={styles['resource-group']}>
                                    <h6
                                        className={`font-l5 font-l5--overline ${styles['resource-group__heading']}`}
                                    >
                                        {group.heading}
                                    </h6>
                                    <div className={styles['resource-group__grid']}>
                                        {group.resources.map((resource) => (
                                            <ResourceCard
                                                key={resource.title}
                                                resource={resource}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className={styles['trust-page__contact']}>
                <div className="container">
                    <div className="row">
                        <div className="col-sm-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2 text-center">
                            <h3 className={styles['trust-page__contact-title']}>
                                Have a question we didn&apos;t answer?
                            </h3>
                            <p
                                className={`font-l5 font-l5--regular ${styles['trust-page__contact-body']}`}
                            >
                                Reach out to our team and we&apos;ll get back to you.
                            </p>
                            <a
                                className={`font-l5 font-l5--subtitle-l--semibold ${styles['trust-page__contact-link']}`}
                                href="mailto:support@lumen5.com"
                            >
                                support@lumen5.com
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
});

export default TrustPage;
